import { PAGE_TYPES } from '@app-types/PageType'
import { initialState, useUserData } from '@contexts/UserDataContext'
import {
  type CloudUserDataBackupEntry,
  getCloudUserDataBackups,
} from 'helpers/cloudUserDataBackup'
import { mergeUserDataRecords } from 'helpers/userDataMerge'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { FiCalendar, FiCloud, FiDatabase, FiRefreshCw } from 'react-icons/fi'

const formatBackupDate = (timestamp: number) => new Date(timestamp).toLocaleString()

const getBackupSummary = (backup: CloudUserDataBackupEntry) => {
  const statsCount = Object.keys(backup.data.stats || {}).length
  const savedPagesCount = backup.data.savedPages?.length || 0
  const lastReadCount = Object.keys(backup.data.lastReadPages || {}).length

  return { statsCount, savedPagesCount, lastReadCount }
}

const hasMeaningfulBackupPage = (value: unknown, defaultValue: unknown) => {
  if (value === null || typeof value === 'undefined') return false
  if (typeof value === 'string' && value.trim().length === 0) return false
  return value !== defaultValue
}

export default function CloudUserDataRecovery() {
  const { userData, updateMultipleUserData } = useUserData()
  const [backups, setBackups] = useState<CloudUserDataBackupEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [showBackups, setShowBackups] = useState(false)

  useEffect(() => {
    const loadBackups = async () => {
      try {
        setLoading(true)
        setBackups(await getCloudUserDataBackups())
      } catch (error) {
        console.error('Failed to load cloud data backups:', error)
      } finally {
        setLoading(false)
      }
    }

    void loadBackups()
  }, [])

  const handleRestore = async (backup: CloudUserDataBackupEntry) => {
    if (
      !window.confirm(
        'Bu bulut yedeğini mevcut cihaz verilerinizle birleştirerek geri yüklemek istediğinize emin misiniz?',
      )
    ) {
      return
    }

    const backupAsAuthoritative = {
      ...backup.data,
      updatedAt: new Date().toISOString(),
    }
    const mergedData = mergeUserDataRecords({
      initialState,
      localData: userData,
      cloudData: backupAsAuthoritative,
      identityData: {
        uid: userData.uid,
        email: userData.email,
        displayName: userData.displayName,
      },
    })

    PAGE_TYPES.forEach((pageType) => {
      const backupPage = backup.data[pageType]
      if (hasMeaningfulBackupPage(backupPage, initialState[pageType])) {
        mergedData[pageType] = backupPage
      }
    })

    updateMultipleUserData(mergedData)
    toast.success('Bulut yedeği mevcut verilerinizle birleştirildi.', {
      duration: 3000,
    })
  }

  if (loading) {
    return (
      <div className="rounded-xl border border-main bg-surface-subtle p-6">
        <div className="flex items-center">
          <FiRefreshCw className="mr-3 h-5 w-5 animate-spin text-brand-secondary" />
          <h3 className="text-lg font-medium text-main">Bulut yedekleri yükleniyor...</h3>
        </div>
      </div>
    )
  }

  if (backups.length === 0) {
    return (
      <div className="rounded-xl border border-main bg-surface-subtle p-6">
        <div className="flex items-center">
          <FiDatabase className="mr-3 h-5 w-5 text-muted" />
          <h3 className="text-lg font-medium text-main">Bulut Veri Yedekleri</h3>
        </div>
        <p className="mt-3 text-sm text-muted">
          Henüz buluttan alınmış bir veri yedeği yok. Giriş yaptığınızda bulut veriniz
          yerel veriyle birleştirilmeden önce otomatik olarak yedeklenir.
        </p>
      </div>
    )
  }

  return (
    <div className="rounded-2xl border border-main bg-main p-6 sacred-shadow">
      <div className="mb-5 flex items-center justify-between">
        <div className="flex items-center">
          <div className="mr-3 rounded-lg bg-info-subtle p-2">
            <FiCloud className="h-5 w-5 text-info" />
          </div>
          <h3 className="text-xl font-semibold text-main">Bulut Veri Kurtarma</h3>
        </div>
      </div>

      <p className="mb-6 text-sm leading-relaxed text-muted">
        Buluttan gelen verileriniz, cihaz verisiyle birleştirilmeden önce yerel olarak
        yedeklenir. Bir sorun fark ederseniz aşağıdaki yedeklerden birini mevcut
        verilerinizle birleştirebilirsiniz.
      </p>

      <div className="mb-2">
        <button
          aria-label="Bulut yedeklerini göster/gizle"
          className="flex w-full items-center justify-between rounded-lg border border-main bg-surface-subtle px-4 py-2 text-sm font-medium text-main transition-colors hover:bg-surface-strong"
          onClick={() => setShowBackups(!showBackups)}
        >
          <span>Bulut Yedekleri ({backups.length})</span>
          <span className="opacity-60">{showBackups ? '▲' : '▼'}</span>
        </button>
      </div>

      {showBackups && (
        <div className="mt-3 overflow-hidden rounded-xl border border-main bg-surface-subtle/50">
          <ul className="divide-y divide-main">
            {backups.map((backup) => {
              const { statsCount, savedPagesCount, lastReadCount } =
                getBackupSummary(backup)

              return (
                <li
                  key={backup.id}
                  className="flex items-center justify-between gap-3 p-4 transition-colors hover:bg-surface-strong"
                >
                  <div>
                    <div className="flex items-center text-sm font-semibold text-main">
                      <FiCalendar className="mr-2 h-4 w-4 text-muted" />
                      {formatBackupDate(backup.timestamp)}
                    </div>
                    <div className="mt-1 text-xs text-muted">
                      {statsCount} günlük istatistik · {savedPagesCount} kayıtlı sayfa ·{' '}
                      {lastReadCount} son okuma
                    </div>
                  </div>
                  <button
                    aria-label={`${formatBackupDate(backup.timestamp)} tarihli bulut yedeğini geri yükle`}
                    onClick={() => handleRestore(backup)}
                    className="rounded-full bg-brand-secondary-subtle px-4 py-1.5 text-xs font-bold text-brand-secondary transition-all hover:bg-brand-secondary/15 hover:text-emerald-900 dark:hover:bg-brand-secondary dark:hover:text-white active:scale-[0.95]"
                  >
                    Birleştir
                  </button>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  )
}
